import React from 'react'
import { Box } from '@mui/material'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import {
  TeamSwimmerVisualization,
  TeamSwimmerVisualizationType,
  QuoteVisualization as QuoteVizType,
  NumberVisualization as NumberVizType,
  PercentVisualization as PercentVizType,
  StarChartVisualization as StarChartVizType,
  TeamHealthHeartVisualization as HeartVizType,
  PartOfWholeVisualization as PartOfWholeVizType,
  ComparisonBarChartVisualization as ComparisonVizType,
  PsychologicalSafetyQuestions
} from '../../../types/TeamSwimmerFeedbackTypes'
import { INTERAX_COLORS } from '../../../constants/colors'
import QuoteVisualization from './visualizations/QuoteVisualization'
import NumberVisualization from './visualizations/NumberVisualization'
import PercentVisualization from './visualizations/PercentVisualization'
import EmotionVisualization from './visualizations/EmotionVisualization'
import StarChartVisualization from './visualizations/StarChartVisualization'
import TeamHealthHeartVisualization from './visualizations/TeamHealthHeartVisualization'
import PartOfWholeVisualization from './visualizations/PartOfWholeVisualization'
import ComparisonBarChartVisualization from './visualizations/ComparisonBarChartVisualization'
import FunnelGraphVisualization from './visualizations/FunnelGraphVisualization'
import type { TeamPerformanceData } from '../../facilitator/components/GenerateReport/visualizations/teamInformationShares/types'
import { TeamInformationSharesLineChartContainer } from '../../facilitator/components/GenerateReport/visualizations/teamInformationShares'
import { formatVariableName } from '../../facilitator/facilitatorReportFlow/helpers/variableNameFormatter'

export interface VisualizationRendererProps {
  /**
   * Team Swimmer visualization data to render
   */
  visualization: TeamSwimmerVisualization
  /**
   * Optional metric name for context
   */
  metricName?: string
  /**
   * Optional variable name to look up visLabel
   */
  variableName?: string
  /**
   * Context for looking up labels (improvement areas vs strengths)
   */
  context?: 'weaknesses' | 'strengths'
  /**
   * Variant for team or individual styling
   */
  variant: 'team' | 'individual'
  /**
   * Additional data from parent (e.g., loops for stacked chart, emotion for emotion viz)
   */
  additionalData?: {
    loops?: Array<{
      loopIndex: number
      players: Array<{
        playerId: string
        displayName: string
        talkedTotal: number
      }>
    }>
    emotion?: string
    questions?: Record<string, number> | PsychologicalSafetyQuestions
    chartData?: TeamPerformanceData[]
  }
}

/**
 * VisualizationRenderer - Renders TeamSwimmer visualizations with proper TypeScript types
 *
 * Features:
 * - Type-safe with NO 'any' types
 * - Uses TeamSwimmerVisualizationType enum
 * - Handles all 8 visualization types from team_swimmer_data.json
 * - Console logs for debugging
 */
export const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({
  visualization,
  metricName,
  variableName,
  context,
  variant,
  additionalData
}) => {
  const { t } = useTranslation()

  switch (visualization.type) {
    case TeamSwimmerVisualizationType.QUOTE: {
      const quoteViz = visualization as QuoteVizType

      return (
        <QuoteVisualization
          text={quoteViz.text}
          speaker={quoteViz.speaker}
          variant={variant}
          additionalData={additionalData}
        />
      )
    }

    case TeamSwimmerVisualizationType.NUMBER: {
      const numberViz = visualization as NumberVizType

      // Look up visLabel from i18n if variableName and context are provided
      let displayTitle = metricName || 'Value'
      if (variableName && context) {
        // Determine prefix based on variant
        const prefix = variant === 'individual' ? 'player' : 'team'
        // Look up from player/team.weaknesses or player/team.strengths based on context and variant

        displayTitle = t(`${prefix}.${context}.${variableName}.visLabel`, {
          ns: 'feedbackTexts',
          defaultValue: metricName || 'Value'
        })
      } else if (metricName === 'inclusionAndInvitations') {
        displayTitle = t('gamer.feedback.performance.inclusionTitle', 'Invitations & Inclusions')
      } else if (metricName) {
        // Format variable name if no translation found
        const formatted = formatVariableName(metricName)
        // Only use formatted name if it's different from the original (meaning formatting was applied)
        if (formatted !== metricName) {
          displayTitle = formatted
        }
      }

      return <NumberVisualization value={numberViz.value} title={displayTitle} description="count" variant={variant} />
    }

    case TeamSwimmerVisualizationType.PERCENT: {
      const percentViz = visualization as PercentVizType

      // Look up visLabel from i18n if variableName and context are provided
      let displayTitle = metricName || 'Value'
      if (variableName && context) {
        // Determine prefix based on variant
        const prefix = variant === 'individual' ? 'player' : 'team'
        // Look up from player/team.weaknesses or player/team.strengths based on context and variant

        displayTitle = t(`${prefix}.${context}.${variableName}.visLabel`, {
          ns: 'feedbackTexts',
          defaultValue: metricName || 'Value'
        })
      } else if (metricName === 'inclusionAndInvitations') {
        displayTitle = t('gamer.feedback.performance.inclusionTitle', 'Invitations & Inclusions')
      } else if (metricName) {
        // Format variable name if no translation found
        const formatted = formatVariableName(metricName)
        // Only use formatted name if it's different from the original (meaning formatting was applied)
        if (formatted !== metricName) {
          displayTitle = formatted
        }
      }

      return (
        <PercentVisualization value={percentViz.value} title={displayTitle} description="count" variant={variant} />
      )
    }

    case TeamSwimmerVisualizationType.EMOTION: {
      const emotionValue = additionalData?.emotion || 'neutral'

      return <EmotionVisualization emotion={emotionValue} variant={variant} />
    }

    case TeamSwimmerVisualizationType.STAR_CHART: {
      const starViz = visualization as StarChartVizType

      const TEAM_REFLECTION_KEYS = [
        'teamReflectionQuestionOne',
        'teamReflectionQuestionTwo',
        'teamReflectionQuestionThree',
        'teamReflectionQuestionFour',
        'teamReflectionQuestionFive',
        'teamReflectionQuestionSix',
        'teamReflectionQuestionSeven',
        'teamReflectionQuestionEight',
        'teamReflectionQuestionNine',
        'teamReflectionQuestionTen',
        'teamReflectionQuestionEleven'
      ]
      const PLAYER_REFLECTION_KEYS = [
        'playerReflectionQuestionOne',
        'playerReflectionQuestionTwo',
        'playerReflectionQuestionThree',
        'playerReflectionQuestionFour',
        'playerReflectionQuestionFive',
        'playerReflectionQuestionSix',
        'playerReflectionQuestionSeven',
        'playerReflectionQuestionEight',
        'playerReflectionQuestionNine',
        'playerReflectionQuestionTen',
        'playerReflectionQuestionEleven'
      ]

      let resolvedLabels: string[] | undefined
      const questionMap = additionalData?.questions as Record<string, number> | undefined
      const questionKeys = questionMap ? Object.keys(questionMap) : undefined
      if (questionKeys && questionKeys.length > 0) {
        const hasPlayerKeys = questionKeys.some((key) => key.startsWith('playerReflectionQuestion'))
        const hasTeamKeys = questionKeys.some((key) => key.startsWith('teamReflectionQuestion'))
        if (hasPlayerKeys) {
          resolvedLabels = PLAYER_REFLECTION_KEYS.filter((key) => questionKeys.includes(key))
        } else if (hasTeamKeys) {
          resolvedLabels = TEAM_REFLECTION_KEYS.filter((key) => questionKeys.includes(key))
        } else {
          resolvedLabels = questionKeys
        }
      }

      return <StarChartVisualization values={starViz.values} labels={resolvedLabels} />
    }

    case TeamSwimmerVisualizationType.TEAM_HEALTH_HEART: {
      const heartViz = visualization as HeartVizType

      return <TeamHealthHeartVisualization value={heartViz.value} variant={variant} />
    }

    case TeamSwimmerVisualizationType.PART_OF_WHOLE: {
      const partViz = visualization as PartOfWholeVizType

      return <PartOfWholeVisualization value={partViz.value} />
    }

    case TeamSwimmerVisualizationType.COMPARISON_BAR_CHART: {
      const compViz = visualization as ComparisonVizType

      // Extract current and previous from values array
      const currentItem = compViz.values.find((v) => v.label === 'current')
      const previousItem = compViz.values.find((v) => v.label === 'previous')

      return (
        <ComparisonBarChartVisualization
          current={currentItem?.value || 0}
          previous={previousItem?.value}
          variant={variant}
          metricName={metricName}
        />
      )
    }

    case TeamSwimmerVisualizationType.STACKED_LINE_CHART_CENTERED: {
      const loops = additionalData?.loops || []

      return <FunnelGraphVisualization loops={loops} />
    }

    case TeamSwimmerVisualizationType.LINE_CHART: {
      const chartData = additionalData?.chartData || []

      return <TeamInformationSharesLineChartContainer data={chartData} />
    }

    default: {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            color: INTERAX_COLORS.white
          }}
        >
          Unknown visualization type: {(visualization as TeamSwimmerVisualization).type}
        </Box>
      )
    }
  }
}

export default VisualizationRenderer
